import React, { useState, useEffect, useMemo } from "react";
import DishCard from "./components/DishCard";
import CategorySlider from "./components/CategorySlider";
import MenuChatWidget from "./components/MenuChatWidget";
import useNav from "./hooks/useNav";
import { useCart } from "./context/CartContext.jsx";
import { useRestaurant } from "./context/RestaurantContext.jsx";
import { useUser } from "./context/UserContext.jsx";
import { AlertCircle, RefreshCw } from "lucide-react";
import DishCardSkeleton from "./components/DishCardSkeleton";
import { toast } from "react-toastify";

const MainPage = () => {
    const [selectedCategory, setSelectedCategory] = useState(null);
    const { restaurant, menu, categories, loading, error, refetch, cachedBranding } = useRestaurant();
    const { user } = useUser();
    const navigate = useNav();
    const cart = useCart();
    const displayName = user?.first_name || user?.username || null;

    useEffect(() => {
        if (categories.length > 0 && !selectedCategory) {
            setSelectedCategory(categories[0].title);
        }
    }, [categories, selectedCategory]);

    const filteredDishes = useMemo(() => {
        if (!selectedCategory || !menu.length) return [];
        return menu.filter((d) => d.category?.toLowerCase() === selectedCategory.toLowerCase());
    }, [menu, selectedCategory]);

    const getCartQuantity = (dishId) => {
        const cartItem = cart.cartItems.find(item => item.id === dishId);
        return cartItem ? cartItem.quantity : 0;
    };

    const handleRetry = async () => {
        try {
            await refetch();
            toast.success("Данные обновлены");
        } catch (err) {
            toast.error("Не удалось обновить данные");
        }
    };

    const handleDishClick = (dish) => {
        navigate("/dish", { state: { dish } });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                {/* Логотип — центр экрана */}
                <div className="flex-1 flex flex-col items-center justify-center gap-4">
                    {cachedBranding?.logo ? (
                        <img
                            src={cachedBranding.logo}
                            alt={cachedBranding.name}
                            className="h-20 w-auto max-w-[180px] object-contain"
                        />
                    ) : (
                        <div className="skeleton w-20 h-20 rounded-2xl" />
                    )}
                    {cachedBranding?.name ? (
                        <p className="font-playfair text-lg font-semibold text-gray-700">
                            {cachedBranding.name}
                        </p>
                    ) : (
                        <div className="skeleton h-5 w-36 rounded" />
                    )}
                    <div className="flex gap-1.5 mt-2">
                        {[0, 1, 2].map(i => (
                            <div
                                key={i}
                                className="w-1.5 h-1.5 rounded-full bg-brandBlue/40 animate-bounce"
                                style={{ animationDelay: `${i * 0.15}s` }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg p-6 shadow-md max-w-md w-full text-center">
                    <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Ошибка загрузки</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <div className="space-y-3">
                        {error.includes('авторизация') && (
                            <button
                                onClick={() => window.location.reload()}
                                className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition"
                            >
                                Перезагрузить для авторизации
                            </button>
                        )}
                        <button
                            onClick={handleRetry}
                            className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition flex items-center justify-center gap-2"
                        >
                            <RefreshCw size={16} />
                            Попробовать снова
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!restaurant) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="text-yellow-500 mx-auto mb-4" size={48} />
                    <p className="text-gray-600 mb-4">Ресторан не найден</p>
                    <button onClick={handleRetry} className="bg-blue-500 text-white px-4 py-2 rounded-lg">
                        Обновить
                    </button>
                </div>
            </div>
        );
    }

    if (!menu.length || !categories.length) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="text-yellow-500 mx-auto mb-4" size={48} />
                    <p className="text-gray-600 mb-4">Меню недоступно</p>
                    <p className="text-sm text-gray-500 mb-4">Попробуйте обновить страницу или зайдите позже</p>
                    <button onClick={handleRetry} className="bg-blue-500 text-white px-4 py-2 rounded-lg">
                        Обновить
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 pb-36 page-enter">


            {/* Быстрые действия — бронь и AI-бот */}
            <div className="px-4 pt-3 mb-2 grid grid-cols-2 gap-2">
                <button
                    onClick={() => navigate("/reservation")}
                    className="flex items-center gap-2.5 bg-brandBlue/8 border border-brandBlue/20 rounded-2xl px-3 py-3 active:scale-[0.97] transition-transform duration-150"
                >
                    <div className="w-8 h-8 rounded-xl bg-brandBlue/15 flex items-center justify-center shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5688b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-gray-900 leading-tight truncate">Забронировать</p>
                        <p className="text-[11px] text-brandBlue mt-0.5 font-medium">Бесплатно</p>
                    </div>
                </button>
                <MenuChatWidget restaurantId={restaurant.id} compact />
            </div>

            {/* Категории */}
            {categories.length > 0 && (
                <div className="px-4 mb-2">
                    <CategorySlider
                        categories={categories}
                        selected={selectedCategory}
                        onSelect={(cat) => setSelectedCategory(cat.title)}
                    />
                </div>
            )}

            {/* Сетка блюд */}
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 px-4">
                {filteredDishes.map((dish) => (
                    <DishCard
                        key={dish.id}
                        id={dish.id}
                        title={dish.title}
                        description={dish.description}
                        price={dish.price}
                        image={dish.image}
                        onViewDetails={() => handleDishClick(dish)}
                        cartQuantity={getCartQuantity(dish.id)}
                        isAvailable={dish.available !== false}
                        avgRating={dish.avg_rating}
                        reviewCount={dish.review_count}
                    />
                ))}
            </div>

            {selectedCategory && filteredDishes.length === 0 && (
                <div className="text-center py-8">
                    <p className="text-gray-500">В этой категории пока нет блюд</p>
                </div>
            )}
        </div>
    );
};

export default MainPage;
